import React, { useRef } from 'react'
import {
  useFloating,
  FloatingPortal,
  FloatingOverlay,
  FloatingFocusManager,
  useInteractions,
  useDismiss,
  useRole,
} from '@floating-ui/react'
import { CSSTransition } from 'react-transition-group'
import classNames from 'classnames'
import SVGIcon from '@components/SVGIcon'
import Button from '@components/Button'
import type { ModalVariant, ModalSize } from '../../types/modal'
import { getFloatingPortalProps } from '@utils/portal_target'

interface FloatingModalProps {
  show: boolean
  onClose: () => void
  title?: string
  firstChildAsHeader?: boolean
  headerContent?: React.ReactNode
  className?: string
  variant?: ModalVariant
  // Controls the max-width via BEM modifiers (see modals.scss)
  size?: ModalSize
  // When true, removes default inner padding so content can reach edges
  fullBleed?: boolean
  children?: React.ReactNode
  transitionTimeout?: number
  onExited?: () => void
}

export default function FloatingModal({
  show,
  onClose,
  title,
  firstChildAsHeader = true,
  headerContent,
  children,
  className,
  variant = 'standard',
  size,
  fullBleed = false,
  transitionTimeout = 200,
  onExited,
}: FloatingModalProps) {
  // We'll autofocus this instead of the close button. This is useful to prevent a focus ring appearing around the close button when the modal opens and we trap focus inside it.
  // Note: If you want to auto focus an element inside the modal, just specify `autoFocus` on that element.
  const modalContentRef = useRef<HTMLDivElement>(null)

  const { refs, context } = useFloating({
    open: show,
    onOpenChange: open => {
      if (!open) onClose()
    },
  })

  const dismiss = useDismiss(context, {
    outsidePress: (event: any): boolean => !event.target.closest('#js-calendar-target'),
  })
  const role = useRole(context)
  const { getFloatingProps } = useInteractions([dismiss, role])

  const handleCSSTransitionExited = () => {
    if (onExited) onExited()
  }

  const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    onClose()
  }

  const isFullBleed = fullBleed

  const modalClasses = classNames(
    'modal qa-modal',
    {
      'modal--full-bleed': isFullBleed,
    },
    size ? `modal--${size}` : null,
    className
  )

  const floatingOverlayClasses = classNames('modal-overlay', {
    'modal-overlay--whiteout': variant === 'whiteout',
    'modal-overlay--blackout': variant === 'blackout',
  })

  let header = null
  let bodyChildren = null
  if (firstChildAsHeader) {
    // Use headerContent or title if provided, otherwise, treat the first child as the header
    const childrenArray = React.Children.toArray(children)
    header = headerContent || (title && <h2 className="modal__title">{title}</h2>) || childrenArray[0]
    bodyChildren = headerContent || title ? children : childrenArray.slice(1)
  } else {
    // Just use children as the body
    bodyChildren = children
  }

  const closeIcon = <SVGIcon xlinkHref="#streamline-delete-1" title="Dismiss" alt="Dismiss" className="icon" />
  const headerSectionContent = header ? (
    <div className="modal__header p-x">
      {header}
      <Button onClick={handleDismiss} className="button-reset btn--icon qa-modal-close">
        {closeIcon}
      </Button>
    </div>
  ) : (
    <Button onClick={handleDismiss} className="button-reset modal__close-standalone p qa-modal-close">
      {closeIcon}
    </Button>
  )

  return (
    <FloatingPortal {...getFloatingPortalProps()}>
      <CSSTransition
        in={show}
        timeout={transitionTimeout}
        classNames="modal-in-out"
        unmountOnExit
        onExited={handleCSSTransitionExited}
      >
        <div>
          <div className="modal-container">
            <FloatingOverlay lockScroll className={floatingOverlayClasses} onClick={onClose} />
            <FloatingFocusManager context={context} modal initialFocus={modalContentRef}>
              <div
                ref={refs.setFloating}
                {...getFloatingProps({
                  className: modalClasses,
                  role: 'dialog',
                  'aria-labelledby': title ? 'modal-title' : undefined,
                  'aria-modal': true,
                })}
              >
                <div className="modal__content modal-initial-focus" ref={modalContentRef} tabIndex={-1}>
                  {headerSectionContent}
                  <div className="modal__scrollable p">{bodyChildren}</div>
                </div>
              </div>
            </FloatingFocusManager>
          </div>
        </div>
      </CSSTransition>
    </FloatingPortal>
  )
}
